import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Spin, Alert, message } from 'antd';
import { useEmbedMode } from '../hooks/useEmbedMode';
import OPMEditorCV from '../components/OPMEditorCV';
import { useOPMDraft, useSaveOPMDraft } from '../api';
import { OntologyApi } from '../api/ontologyApi';
import { useAppStore } from '../store';
import {
  toPlainFormat,
  toOPMEditorFormat,
} from '../components/OPMEditorCV/utils/opm-data-adapter';
import {
  normalizeGraphTree,
  createGraphPayload,
} from '../components/OPMEditor/utils/graphLayerHelpers';

const OPMEditorCVPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ontologyId = searchParams.get('id');
  const { isEmbedMode } = useEmbedMode();
  // 统一加载态容器：embed 模式顶部导航 40px，正常模式 64px
  const headerHeight = isEmbedMode ? 40 : 64;
  const loadingContainerStyle = {
    height: `calc(100vh - ${headerHeight}px)`,
  } as const;

  // Zustand store
  const { setCurrentOntology, currentOntology } = useAppStore();

  // 状态管理
  const [currentHash, setCurrentHash] = useState<string>('');
  const currentHashRef = useRef('');
  const [isDataReady, setIsDataReady] = useState(false);
  const [initialPlainData, setInitialPlainData] = useState<string | undefined>(
    undefined
  );

  // 数据获取
  const {
    data: draftData,
    isLoading,
    error,
    refetch,
  } = useOPMDraft(ontologyId ? parseInt(ontologyId) : 0, !!ontologyId);

  // 保存数据
  const saveMutation = useSaveOPMDraft(ontologyId ? parseInt(ontologyId) : 0);

  // 当数据加载完成时，转换为 Plain 格式
  useEffect(() => {
    console.log('🔄 OPMEditorCVPage: 数据加载状态变化:', draftData);

    if (draftData) {
      if (draftData.data) {
        // 将 OPMEditor 格式转换为 Plain 格式
        const normalizedGraph = normalizeGraphTree(draftData.data.graph);
        console.log('📊 OPMEditorCVPage: 转换前数据:', {
          hash: draftData.data.hash,
          layers: normalizedGraph.length,
        });

        // 构建 OPMEditor 格式的数据结构进行转换
        // OntologyGraphPayload 格式: { version: 'v2', data: OntologyGraphTree }
        const opmEditorData = {
          version: 'v2',
          data: normalizedGraph,
        };

        const plainData = toPlainFormat(opmEditorData);
        if (plainData) {
          console.log('✅ OPMEditorCVPage: 转换为 Plain 格式成功');
          setInitialPlainData(JSON.stringify(plainData));
        } else {
          console.log('📊 OPMEditorCVPage: 转换失败，使用空画板');
          setInitialPlainData(undefined);
        }

        setCurrentHash(draftData.data.hash || '');
        currentHashRef.current = draftData.data.hash || '';
        setIsDataReady(true);
      } else {
        console.log('📊 OPMEditorCVPage: 无返回数据，使用空画板');
        setCurrentHash('');
        currentHashRef.current = '';
        setInitialPlainData(undefined);
        setIsDataReady(true);
      }
    }
  }, [draftData]);

  // 获取本体详情信息
  useEffect(() => {
    const fetchOntologyDetail = async () => {
      if (ontologyId) {
        try {
          console.log('🚀 获取本体详情信息:', ontologyId);
          const detail = await OntologyApi.getOntologyDetail(
            parseInt(ontologyId)
          );
          console.log('✅ 本体详情获取成功:', detail.name);

          setCurrentOntology({
            id: detail.id,
            name: detail.name,
            canEdit: detail.canEdit,
          });
        } catch (error) {
          console.error('❌ 获取本体详情失败:', error);
          setCurrentOntology(null);
        }
      }
    };

    fetchOntologyDetail();

    return () => {
      setCurrentOntology(null);
    };
  }, [ontologyId, setCurrentOntology]);

  // 保存操作处理
  const handleSave = async (plainData: any, options?: { silent?: boolean }) => {
    console.log('📤 准备保存，当前hash值:', currentHashRef.current);

    // 将 Plain 格式转换为 OPMEditor 格式
    const opmEditorData = toOPMEditorFormat(plainData);
    if (!opmEditorData) {
      message.error('保存失败：数据转换错误');
      throw new Error('保存失败：数据转换错误');
    }

    console.log('📊 转换后的 OPMEditor 格式数据:', opmEditorData);

    // 构建保存请求（opmEditorData 已经是 OntologyGraphPayload 格式）
    const payload = createGraphPayload(opmEditorData.data);
    const saveRequest: any = { graph: payload };
    if (currentHashRef.current) {
      saveRequest.hash = currentHashRef.current;
    }

    try {
      const response = await saveMutation.mutateAsync(saveRequest);
      if (!options?.silent) {
        message.success('保存成功');
      }
      setCurrentHash(response.data);
      currentHashRef.current = response.data;
      console.log('✅ 保存成功，新的hash值:', response.data);
      return response;
    } catch (error: any) {
      message.error('保存失败: ' + error.message);
      throw error;
    }
  };

  // 如果没有提供ID，显示空画板（非强制要求ID）
  if (!ontologyId) {
    return (
      <div className="h-full w-full">
        <div
          className={`opm-editor-cv-root h-full${isEmbedMode ? ' embed-mode' : ''}`}
        >
          <OPMEditorCV ontologyId={ontologyId} />
        </div>
      </div>
    );
  }

  // 加载状态
  if (isLoading) {
    return (
      <div
        className="w-full flex items-center justify-center"
        style={loadingContainerStyle}
      >
        <div className="flex flex-col items-center justify-center">
          <Spin size="large" />
          {/* <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.65)' }}>
            正在加载数据...
          </div> */}
        </div>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <Alert
          message="数据加载失败"
          description={error.message || '无法获取本体数据'}
          type="error"
          showIcon
          action={
            <button
              onClick={() => refetch()}
              className="ant-btn ant-btn-primary"
            >
              重试
            </button>
          }
        />
      </div>
    );
  }

  // 数据准备就绪，渲染编辑器
  return (
    <div className="h-full w-full">
      <div
        className={`opm-editor-cv-root h-full${isEmbedMode ? ' embed-mode' : ''}`}
      >
        {isDataReady ? (
          <OPMEditorCV
            initialData={initialPlainData}
            ontologyId={ontologyId}
            onSave={handleSave}
            isSaving={saveMutation.isPending}
            canEdit={currentOntology?.canEdit ?? true}
          />
        ) : (
          <div
            className="w-full flex items-center justify-center"
            style={loadingContainerStyle}
          >
            <div className="flex flex-col items-center justify-center">
              <Spin size="large" />
              {/* <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.65)' }}>
                准备编辑器...
              </div> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OPMEditorCVPage;
